import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Clock, MapPin, Compass, ArrowRight, ArrowLeft, Sparkles } from "lucide-react";
import SmartSearch from "@/components/SmartSearch";
import axios from "axios";
import logger from '@/utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TourCard = ({ tour, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: Math.min(index * 0.1, 0.5) }}
  >
    <Card className="group overflow-hidden h-full flex flex-col hover:shadow-xl transition-shadow duration-300" data-testid={`tour-card-${tour.tour_code}`}>
      <CardHeader className="p-0 relative overflow-hidden">
        <img
          src={tour.image}
          alt={tour.title}
          className="w-full h-56 object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-4 left-4 flex gap-2">
          <span className="px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full text-xs font-medium text-slate-700 uppercase">
            {tour.category}
          </span>
        </div>
        <div className="absolute bottom-4 right-4">
          <span className="px-3 py-1 bg-coral text-white rounded-full text-sm font-semibold">
            ₹{tour.price?.toLocaleString()}
          </span>
        </div>
      </CardHeader>
      <CardContent className="p-6 flex-1">
        <h3 className="font-heading text-xl font-semibold text-primary mb-2 group-hover:text-coral transition-colors">
          {tour.title}
        </h3>
        <div className="flex items-center gap-4 text-sm text-slate-600 mb-4">
          <span className="flex items-center gap-1">
            <MapPin className="h-4 w-4" />
            {tour.destination}
          </span>
          <span className="flex items-center gap-1">
            <Clock className="h-4 w-4" />
            {tour.duration}
          </span>
        </div>
        {tour.highlights && tour.highlights.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {tour.highlights.slice(0, 3).map((highlight) => (
              <span key={highlight} className="px-2 py-1 bg-slate-100 rounded text-xs text-slate-600">
                {highlight}
              </span>
            ))}
            {tour.highlights.length > 3 && (
              <span className="px-2 py-1 bg-slate-100 rounded text-xs text-slate-600">
                +{tour.highlights.length - 3} more
              </span>
            )}
          </div>
        )}
      </CardContent>
      <CardFooter className="p-6 pt-0">
        <Link to={`/theme-tours/${tour.tour_code}`} className="w-full">
          <Button className="w-full btn-coral group" data-testid={`view-tour-${tour.tour_code}`}>
            Explore Tour
            <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </Button>
        </Link>
      </CardFooter>
    </Card>
  </motion.div>
);

const ThemeToursPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [tours, setTours] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchResults, setSearchResults] = useState(null); // null = no search active

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [toursRes, categoriesRes] = await Promise.all([
          axios.get(`${API}/theme-tours`),
          axios.get(`${API}/theme-tour-categories`)
        ]);
        setTours(toursRes.data);
        setCategories((categoriesRes.data || []).filter(c => c.is_active));

        // Check URL params for pre-selected category
        const catParam = searchParams.get("theme_category");
        if (catParam) {
          const found = (categoriesRes.data || []).find(c => c.id === catParam || c.slug === catParam);
          if (found) setSelectedCategory(found);
        }
      } catch (error) {
        logger.error("Error fetching theme tours:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [searchParams]);

  // Handle search from SmartSearch — show matching theme tours directly
  const handleSearch = (_packageResults, filters, themeTourResults = []) => {
    const hasActiveSearch = Object.values(filters).some(v => v && (Array.isArray(v) ? v[1] < 500000 : true));
    if (hasActiveSearch || themeTourResults.length > 0) {
      setSearchResults(themeTourResults);
      setSelectedCategory(null); // exit category detail view
    } else {
      setSearchResults(null); // clear search, back to categories
    }
  };

  const clearSearch = () => {
    setSearchResults(null);
  };

  const toursInCategory = selectedCategory
    ? tours.filter(t => t.theme_category === selectedCategory.id)
    : [];

  return (
    <div data-testid="theme-tours-page">
      {/* Hero Section */}
      <section className="relative pt-32 pb-24 bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=1600')] bg-cover bg-center opacity-20" />
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <span className="caption text-purple-300 mb-4 block flex items-center justify-center gap-2">
              <Sparkles className="h-4 w-4" /> Curated Experiences
            </span>
            <h1 className="heading-1 text-white mb-6">
              {selectedCategory ? selectedCategory.name : 'Theme Tours'}
            </h1>
            <p className="text-lg text-white/80 mb-8">
              {selectedCategory
                ? `Explore our ${selectedCategory.name} travel packages`
                : 'Discover unique travel experiences crafted around your interests'}
            </p>

            {/* Smart Search */}
            {!selectedCategory && (
              <SmartSearch
                packages={[]}
                themeTours={tours}
                onSearch={handleSearch}
                className="max-w-3xl mx-auto"
                searchType="theme"
              />
            )}
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="section-padding bg-accent">
        <div className="container-custom">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
              <p className="mt-4 text-slate-600">Loading...</p>
            </div>
          ) : searchResults !== null ? (
            /* === SEARCH RESULTS: show matching tours directly === */
            <div>
              <div className="flex items-center justify-between mb-6">
                <p className="text-slate-600">
                  {searchResults.length > 0
                    ? <><span className="font-semibold text-slate-900">{searchResults.length}</span> matching tour{searchResults.length > 1 ? 's' : ''} found</>
                    : 'No matching tours found'}
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearSearch}
                  className="text-purple-600 hover:text-purple-700"
                  data-testid="clear-search"
                >
                  Clear Search
                </Button>
              </div>

              {searchResults.length === 0 ? (
                <div className="text-center py-16">
                  <Compass className="h-16 w-16 mx-auto text-slate-300 mb-4" />
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">No Tours Found</h3>
                  <p className="text-slate-600 mb-6">Try a different search term or browse categories.</p>
                  <Button onClick={clearSearch} className="bg-purple-600 hover:bg-purple-700">
                    Browse All Themes
                  </Button>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="search-results-grid">
                  {searchResults.map((tour, index) => (
                    <TourCard key={tour.id} tour={tour} index={index} />
                  ))}
                </div>
              )}
            </div>
          ) : selectedCategory ? (
            /* === CATEGORY DETAIL: show tours inside selected category === */
            <div>
              <Button
                variant="ghost"
                onClick={() => setSelectedCategory(null)}
                className="mb-6 text-purple-600 hover:text-purple-700 -ml-2"
                data-testid="back-to-categories"
              >
                <ArrowLeft className="h-4 w-4 mr-2" /> All Theme Tours
              </Button>

              {toursInCategory.length === 0 ? (
                <div className="text-center py-16">
                  <Compass className="h-16 w-16 mx-auto text-slate-300 mb-4" />
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">No Tours Yet</h3>
                  <p className="text-slate-600 mb-6">
                    Tours for {selectedCategory.name} are coming soon.
                  </p>
                  <Button
                    onClick={() => setSelectedCategory(null)}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    Browse Other Themes
                  </Button>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {toursInCategory.map((tour, index) => (
                    <TourCard key={tour.id} tour={tour} index={index} />
                  ))}
                </div>
              )}
            </div>
          ) : (
            /* === CATEGORY LISTING: show theme category cards === */
            <div>
              {categories.length === 0 ? (
                <div className="text-center py-16">
                  <Compass className="h-16 w-16 mx-auto text-slate-300 mb-4" />
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">Coming Soon</h3>
                  <p className="text-slate-600">Theme tour categories are being curated. Check back soon!</p>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="category-grid">
                  {categories.map((cat, index) => {
                    const count = tours.filter(t => t.theme_category === cat.id).length;
                    return (
                      <motion.div
                        key={cat.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: Math.min(index * 0.08, 0.5) }}
                      >
                        <Card
                          className="group cursor-pointer hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-purple-200 overflow-hidden"
                          onClick={() => setSelectedCategory(cat)}
                          data-testid={`category-card-${cat.slug || cat.id}`}
                        >
                          <CardContent className="p-6 text-center">
                            <div
                              className="w-16 h-16 rounded-2xl flex items-center justify-center text-2xl mx-auto mb-4 group-hover:scale-110 transition-transform duration-300"
                              style={{ backgroundColor: (cat.color || '#8b5cf6') + '15', color: cat.color || '#8b5cf6' }}
                            >
                              {cat.icon || '🎯'}
                            </div>
                            <h3 className="font-heading text-lg font-semibold text-slate-900 group-hover:text-purple-700 transition-colors mb-2">
                              {cat.name}
                            </h3>
                            <div className="flex items-center justify-center gap-1 text-purple-600 text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                              Explore <ArrowRight className="h-3.5 w-3.5" />
                            </div>
                          </CardContent>
                        </Card>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-purple-600 to-coral">
        <div className="container-custom text-center">
          <h2 className="heading-2 text-white mb-4">Can&apos;t Find Your Perfect Theme?</h2>
          <p className="text-white/90 mb-8 max-w-xl mx-auto">
            Tell us about your travel dreams and we&apos;ll craft a custom themed experience just for you.
          </p>
          <Link to="/get-quote">
            <Button className="bg-white text-coral hover:bg-white/90 px-8 py-3" data-testid="custom-tour-cta">
              Request Custom Theme Tour
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ThemeToursPage;
